import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaEnvelope, FaEye, FaPhone, FaTrash, FaWhatsapp } from "react-icons/fa";
import axiosInstance from "../components/api/axiosInstance";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import { mobileValidationMessage, sanitizeMobileInput } from "./portalPhone";
import { canDeleteInPortal, portalLoginEmailHint } from "./portalSession";
import { SupportSortableHeader } from "../pages/SupportSortableHeader";
import PortalAlert from "./PortalAlert";
import PortalFieldInfo from "./PortalFieldInfo";
import PortalFormPage from "./PortalFormPage";
import PortalConfirmModal from "./PortalConfirmModal";
import PortalResultsHead from "./PortalResultsHead";
import { formatPhone } from "../utils/formatters";
import { PASSWORD_POLICY_HINT, validatePasswordStrength } from "../utils/passwordPolicy";
import PortalFiltersCollapsible, { usePortalCompactFilters } from "./PortalFiltersCollapsible";
import "../assets/css/DigixLeads.css";
import PortalSelect from "./PortalSelect";

const PORTAL_EMPLOYEES_ROWS_PER_PAGE_KEY = "portalEmployeesRowsPerPage";
const PORTAL_EMPLOYEES_CURRENT_PAGE_KEY = "portalEmployeesCurrentPage";

function display(value) {
  if (value == null || String(value).trim() === "") return "—";
  return String(value);
}

function fullName(row) {
  return `${(row.First_Name || "").trim()} ${(row.Last_Name || "").trim()}`.trim() || "—";
}

function rolesDisplay(row) {
  if (Array.isArray(row?.roles) && row.roles.length) {
    return row.roles
      .map((r) => r.role_name || r.Role_Name || r.role || r.name)
      .filter(Boolean)
      .join(", ");
  }
  if (row?.Roles) return String(row.Roles);
  return "";
}

function phoneDisplay(row) {
  if (!row?.Phone_Number) return "—";
  const digits = `${row.Country_Code || ""}${row.Phone_Number}`.replace(/\D/g, "");
  return formatPhone(digits) || "—";
}

function emailLink(email) {
  const value = String(email || "").trim();
  if (!value) return null;
  return (
    <a href={`mailto:${value}`} onClick={(e) => e.stopPropagation()}>
      {value}
    </a>
  );
}

function phoneLink(row) {
  const label = phoneDisplay(row);
  if (label === "—") return null;
  const tel = `${row.Country_Code || ""}${row.Phone_Number}`.replace(/[^\d+]/g, "");
  return (
    <a href={`tel:${tel}`} onClick={(e) => e.stopPropagation()}>
      {label}
    </a>
  );
}

function statusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "active") return "cportal-badge cportal-badge--success";
  if (s === "inactive") return "cportal-badge cportal-badge--danger";
  return "cportal-badge";
}

function employeeMatchesQuery(emp, q) {
  if (!q) return true;
  return [
    emp.Full_Name || fullName(emp),
    emp.First_Name,
    emp.Last_Name,
    emp.Employee_Id,
    emp.Company_Email,
    emp.Personal_Email,
    emp.Phone_Number,
    emp.Country_Code,
    emp.Designation,
    emp.Department,
    emp.Status,
    rolesDisplay(emp),
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(q));
}

const SORT_ACCESSORS = {
  name: (e) => fullName(e).toLowerCase(),
  employeeId: (e) => (e.Employee_Id || "").toLowerCase(),
  designation: (e) => (e.Designation || "").toLowerCase(),
  roles: (e) => rolesDisplay(e).toLowerCase(),
  department: (e) => (e.Department || "").toLowerCase(),
  companyEmail: (e) => (e.Company_Email || "").toLowerCase(),
  personalEmail: (e) => (e.Personal_Email || "").toLowerCase(),
  phone: (e) => (e.Phone_Number || "").toLowerCase(),
  status: (e) => (e.Status || "").toLowerCase(),
};

const COLUMNS = [
  { key: "name", label: "Name" },
  { key: "employeeId", label: "Employee ID" },
  { key: "designation", label: "Designation" },
  { key: "roles", label: "Roles" },
  { key: "department", label: "Department" },
  { key: "companyEmail", label: "Company Email" },
  { key: "personalEmail", label: "Personal Email" },
  { key: "phone", label: "Phone" },
  { key: "status", label: "Status" },
];

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  companyEmail: "",
  password: "",
  personalEmail: "",
  employeeId: "",
  phoneNumber: "",
  department: "",
  designationId: "",
  roleIds: [],
  gender: "",
  doj: "",
};

const GENDER_OPTIONS = ["Male", "Female", "Other", "Prefer not to say"];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateEmployeeForm(form) {
  if (!form.firstName.trim()) {
    return "First name is required.";
  }
  if (!form.companyEmail.trim()) {
    return "Company email is required.";
  }
  if (!EMAIL_PATTERN.test(form.companyEmail.trim())) {
    return "Enter a valid company email.";
  }
  if (form.personalEmail.trim() && !EMAIL_PATTERN.test(form.personalEmail.trim())) {
    return "Enter a valid personal email.";
  }
  if (!form.employeeId.trim()) {
    return "Employee ID is required.";
  }
  const phoneError = mobileValidationMessage(form.phoneNumber, { required: true });
  if (phoneError) return phoneError;
  if (!form.designationId) {
    return "Designation is required.";
  }
  if (!Array.isArray(form.roleIds) || form.roleIds.length === 0) {
    return "At least one role is required.";
  }
  // Blank is allowed: someone who already has a login (e.g. at another company) keeps their
  // password; the API asks for one when the company email is new.
  if (!form.password.trim()) return "";
  return validatePasswordStrength(form.password) || "";
}

function TenantEmployees({ createMode = false }) {
  const navigate = useNavigate();
  const { filtersOpen, setFiltersOpen, isCompactViewport } = usePortalCompactFilters();
  const [employees, setEmployees] = useState([]);
  const [tenantId, setTenantId] = useState(null);
  const [designations, setDesignations] = useState([]);
  const [roles, setRoles] = useState([]);
  const [domainName, setDomainName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [currentPage, setCurrentPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_EMPLOYEES_CURRENT_PAGE_KEY);
      if (!saved) return 1;
      const parsed = parseInt(saved, 10);
      return parsed > 0 ? parsed : 1;
    } catch {
      return 1;
    }
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PORTAL_EMPLOYEES_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) return parsed;
    } catch {
      /* ignore */
    }
    return 10;
  });
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [resultKeyword, setResultKeyword] = useState("");
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const canDelete = useMemo(() => canDeleteInPortal(), []);

  const toggleSort = (key) => {
    setCurrentPage(1);
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(PORTAL_EMPLOYEES_ROWS_PER_PAGE_KEY, String(next));
  };

  const loadEmployees = useCallback(async () => {
    const res = await axiosInstance.get("/client/employees");
    setEmployees(Array.isArray(res.data?.data) ? res.data.data : []);
    const tid = res.data?.tenant_id ?? res.data?.Tenant_Id ?? null;
    if (tid != null) setTenantId(tid);
    return tid;
  }, []);

  const loadMeta = useCallback(async () => {
    const res = await axiosInstance.get("/client/employee-meta");
    const data = res.data?.data || {};
    const tid = data.tenant_id ?? data.Tenant_Id ?? null;
    if (tid != null) setTenantId(tid);
    setDesignations(Array.isArray(data.designations) ? data.designations : []);
    setRoles(Array.isArray(data.roles) ? data.roles : []);
    setDomainName(data.Domain_Name || data.domain_name || "");
    return tid;
  }, []);

  const toggleRoleId = (roleId) => {
    const idStr = String(roleId);
    setForm((prev) => {
      const current = Array.isArray(prev.roleIds) ? prev.roleIds : [];
      const next = current.includes(idStr)
        ? current.filter((id) => id !== idStr)
        : [...current, idStr];
      return { ...prev, roleIds: next };
    });
  };

  useEffect(() => {
    if (createMode) return undefined;
    let active = true;
    (async () => {
      try {
        await loadEmployees();
      } catch (_) {
        if (active) setError("Could not load employees.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [loadEmployees, createMode]);

  useEffect(() => {
    if (!createMode) return;
    setFormError("");
    setForm(EMPTY_FORM);
    loadMeta().catch(() => setFormError("Could not load form options."));
  }, [createMode, loadMeta]);

  const filtered = useMemo(() => {
    let list = employees;
    if (statusFilter) {
      list = list.filter(
        (emp) => String(emp.Status || "").toLowerCase() === statusFilter.toLowerCase()
      );
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((emp) => employeeMatchesQuery(emp, q));
  }, [employees, search, statusFilter]);

  const resultFiltered = useMemo(() => {
    const q = resultKeyword.trim().toLowerCase();
    if (!q) return filtered;
    return filtered.filter((emp) => employeeMatchesQuery(emp, q));
  }, [filtered, resultKeyword]);

  const sorted = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortKey];
    if (!accessor) return resultFiltered;
    const dir = sortDir === "asc" ? 1 : -1;
    return [...resultFiltered].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [resultFiltered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const pageRows = sorted.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    if (currentPage > 0) {
      localStorage.setItem(PORTAL_EMPLOYEES_CURRENT_PAGE_KEY, String(currentPage));
    }
  }, [currentPage]);

  const skipFilterPageResetRef = React.useRef(true);
  useEffect(() => {
    if (skipFilterPageResetRef.current) {
      skipFilterPageResetRef.current = false;
      return;
    }
    setCurrentPage(1);
  }, [search, resultKeyword, statusFilter]);

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const closeAddForm = () => navigate("/portal/employees");

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  const requestDeactivate = (emp) => {
    if (emp.Status === "Inactive") return;
    setDeactivateTarget(emp);
  };

  const closeDeactivateModal = () => {
    if (deactivatingId != null) return;
    setDeactivateTarget(null);
  };

  const confirmDeactivate = async () => {
    const emp = deactivateTarget;
    if (!emp?.Tenant_User_Id || emp.Status === "Inactive") return;

    setDeactivatingId(emp.Tenant_User_Id);
    setError("");
    try {
      await axiosInstance.delete(`/client/employees/${emp.Tenant_User_Id}`);
      setDeactivateTarget(null);
      await loadEmployees();
    } catch (err) {
      setError(err.response?.data?.message || "Could not deactivate employee.");
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validateEmployeeForm(form);
    if (validationError) {
      setFormError(validationError);
      return;
    }
    if (!tenantId) {
      setFormError("Your company tenant is not set up. Contact support.");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      await axiosInstance.post("/client/employees", {
        Tenant_Id: tenantId,
        First_Name: form.firstName.trim(),
        Last_Name: form.lastName.trim() || null,
        Company_Email: form.companyEmail.trim(),
        Password: form.password.trim() || null,
        Personal_Email: form.personalEmail.trim() || null,
        Employee_Id: form.employeeId.trim(),
        Phone_Number: form.phoneNumber.trim(),
        Country_Code: "+91",
        Department: form.department.trim() || null,
        Designation_Id: Number(form.designationId),
        Role_Ids: form.roleIds.map((id) => Number(id)).filter((n) => !Number.isNaN(n)),
        Gender: form.gender.trim() || null,
        DOJ: form.doj.trim() || null,
        Status: "Active",
      });
      closeAddForm();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not create employee."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cportal-page">
      {!createMode && (
        <>
      <div className="cportal-page-head">
        <h1>Employees</h1>
      </div>

      <PortalAlert type="error" message={error} onClear={() => setError("")} />

      <PortalFiltersCollapsible
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search name, email, phone"
        searchAriaLabel="Search employees"
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        isCompactViewport={isCompactViewport}
        hasActiveFilters={Boolean(search || statusFilter)}
        onClear={() => {
          setSearch("");
          setStatusFilter("");
          setCurrentPage(1);
        }}
        actions={
          <button
            type="button"
            className="cportal-btn cportal-btn-primary"
            onClick={() => navigate("/portal/employees/new")}
          >
            Add Employee
          </button>
        }
      >
        <div className="cportal-filter-row">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            aria-label="Filter by status"
          >
            <option value="">All Status</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </div>
      </PortalFiltersCollapsible>

      <div className="cportal-card">
        <PortalResultsHead
          heading="Employees"
          count={sorted.length}
          keyword={resultKeyword}
          onKeywordChange={setResultKeyword}
        />

        {loading ? (
          <p className="cportal-muted">Loading…</p>
        ) : employees.length === 0 ? (
          <p className="cportal-muted">No employees are registered for your company yet.</p>
        ) : sorted.length === 0 ? (
          <p className="cportal-muted">No employees match your search.</p>
        ) : (
          <>
          <div className="cportal-table-wrap co-desktop-table">
            <table className="cportal-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} className="cportal-th-sortable">
                      <SupportSortableHeader
                        columnId={col.key}
                        label={col.label}
                        sortColumn={sortKey}
                        sortDirection={sortDir}
                        onSort={toggleSort}
                      />
                    </th>
                  ))}
                  <th className="cportal-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((emp) => (
                  <tr key={emp.Tenant_User_Id}>
                    <td>
                      <button
                        type="button"
                        className="cportal-table-link"
                        onClick={() => navigate(`/portal/employees/${emp.Tenant_User_Id}`)}
                      >
                        {display(emp.Full_Name || fullName(emp))}
                      </button>
                    </td>
                    <td>{display(emp.Employee_Id)}</td>
                    <td>{display(emp.Designation)}</td>
                    <td>{display(rolesDisplay(emp))}</td>
                    <td>{display(emp.Department)}</td>
                    <td>{display(emp.Company_Email)}</td>
                    <td>{display(emp.Personal_Email)}</td>
                    <td>{phoneDisplay(emp)}</td>
                    <td>
                      <span className={statusBadgeClass(emp.Status)}>
                        {display(emp.Status)}
                      </span>
                    </td>
                    <td className="cportal-td-actions">
                      <div className="cportal-re-actions">
                        {(() => {
                          const phoneDigits = `${emp.Country_Code || ""}${emp.Phone_Number || ""}`.replace(
                            /[^\d+]/g,
                            ""
                          );
                          const telHref = emp.Phone_Number ? `tel:${phoneDigits}` : null;
                          const waDigits = phoneDigits.replace(/\D/g, "");
                          const waHref =
                            emp.Phone_Number && waDigits.length >= 10 ? `https://wa.me/${waDigits}` : null;
                          const email = emp.Company_Email || emp.Personal_Email;
                          const mailHref = email ? `mailto:${email}` : null;
                          return (
                            <>
                              {waHref ? (
                                <a
                                  className="re-lead-action re-lead-action--whatsapp"
                                  href={waHref}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={`WhatsApp ${phoneDisplay(emp)}`}
                                  aria-label="Open WhatsApp"
                                >
                                  <FaWhatsapp size={15} />
                                </a>
                              ) : (
                                <span
                                  className="re-lead-action re-lead-action--whatsapp re-lead-action--disabled"
                                  title="WhatsApp unavailable"
                                  aria-disabled="true"
                                >
                                  <FaWhatsapp size={15} />
                                </span>
                              )}
                              {telHref ? (
                                <a
                                  className="re-lead-action re-lead-action--call"
                                  href={telHref}
                                  title={`Call ${phoneDisplay(emp)}`}
                                  aria-label="Call employee"
                                >
                                  <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                                </a>
                              ) : (
                                <span
                                  className="re-lead-action re-lead-action--call re-lead-action--disabled"
                                  title="Call unavailable"
                                  aria-disabled="true"
                                >
                                  <FaPhone size={12} style={{ transform: "scaleX(-1)" }} />
                                </span>
                              )}
                              {mailHref ? (
                                <a
                                  className="re-lead-action re-lead-action--email"
                                  href={mailHref}
                                  title={`Email ${email}`}
                                  aria-label="Email employee"
                                >
                                  <FaEnvelope size={13} />
                                </a>
                              ) : (
                                <span
                                  className="re-lead-action re-lead-action--email re-lead-action--disabled"
                                  title="Email unavailable"
                                  aria-disabled="true"
                                >
                                  <FaEnvelope size={13} />
                                </span>
                              )}
                            </>
                          );
                        })()}
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--view"
                          onClick={() => navigate(`/portal/employees/${emp.Tenant_User_Id}`)}
                          title="View employee"
                          aria-label="View employee"
                        >
                          <FaEye size={16} />
                        </button>
                        <button
                          type="button"
                          className="re-lead-action re-lead-action--edit"
                          onClick={() =>
                            navigate(`/portal/employees/${emp.Tenant_User_Id}`, {
                              state: { edit: true },
                            })
                          }
                          title="Edit employee"
                          aria-label="Edit employee"
                        >
                          <FaEdit size={16} />
                        </button>
                        {canDelete && (
                          <button
                            type="button"
                            className="re-lead-action re-lead-action--delete"
                            onClick={() => requestDeactivate(emp)}
                            disabled={
                              emp.Status === "Inactive" ||
                              deactivatingId === emp.Tenant_User_Id
                            }
                            title="Delete employee"
                            aria-label="Delete employee"
                          >
                            <FaTrash size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ResultsMobileList
            emptyLabel="No employees match your search."
            className="cportal-employees-mobile"
          >
            {pageRows.map((emp) => {
              const id = emp.Tenant_User_Id;
              const name = emp.Full_Name || fullName(emp);
              const status = emp.Status || "—";
              const statusTone =
                String(status).toLowerCase() === "active"
                  ? "success"
                  : String(status).toLowerCase() === "inactive"
                    ? "danger"
                    : "neutral";
              const roles = rolesDisplay(emp);
              const phoneDigits = `${emp.Country_Code || ""}${emp.Phone_Number || ""}`.replace(
                /[^\d+]/g,
                ""
              );
              const telHref = emp.Phone_Number ? `tel:${phoneDigits}` : null;
              const waDigits = phoneDigits.replace(/\D/g, "");
              const waHref = emp.Phone_Number && waDigits.length >= 10 ? `https://wa.me/${waDigits}` : null;
              return (
                <ResultsMobileCard
                  key={id}
                  title={display(name)}
                  subtitle={emp.Designation || emp.Employee_Id || null}
                  meta={[
                    { label: status, tone: statusTone, status: true },
                    emp.Department ? { label: emp.Department, tone: "info" } : null,
                  ].filter(Boolean)}
                  fieldsLayout="stacked"
                  fields={[
                    { label: "Roles", value: roles || null },
                    { label: "Company Email", value: emailLink(emp.Company_Email) },
                    { label: "Phone", value: phoneLink(emp) },
                    { label: "Personal Email", value: emailLink(emp.Personal_Email) },
                  ]}
                  onOpen={() => navigate(`/portal/employees/${id}`)}
                  actions={
                    <div className="co-mcard__action-grid">
                      {telHref ? (
                        <a className="co-maction" href={telHref} title="Call" aria-label="Call">
                          <FaPhone size={14} style={{ transform: "scaleX(-1)" }} />
                          <span>Call</span>
                        </a>
                      ) : null}
                      {waHref ? (
                        <a
                          className="co-maction co-maction--whatsapp"
                          href={waHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="WhatsApp"
                          aria-label="WhatsApp"
                        >
                          <FaWhatsapp size={14} />
                          <span>WA</span>
                        </a>
                      ) : null}
                      {emp.Company_Email || emp.Personal_Email ? (
                        <a
                          className="co-maction"
                          href={`mailto:${emp.Company_Email || emp.Personal_Email}`}
                          title="Email"
                          aria-label="Email"
                        >
                          <FaEnvelope size={14} />
                          <span>Email</span>
                        </a>
                      ) : null}
                      <button
                        type="button"
                        className="co-maction"
                        title="View"
                        aria-label="View"
                        onClick={() => navigate(`/portal/employees/${id}`)}
                      >
                        <FaEye size={14} />
                        <span>View</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() =>
                          navigate(`/portal/employees/${id}`, { state: { edit: true } })
                        }
                      >
                        <FaEdit size={14} />
                        <span>Edit</span>
                      </button>
                      {canDelete ? (
                        <button
                          type="button"
                          className="co-maction co-maction--danger"
                          title="Delete"
                          aria-label="Delete"
                          disabled={
                            emp.Status === "Inactive" || deactivatingId === id
                          }
                          onClick={() => requestDeactivate(emp)}
                        >
                          <FaTrash size={14} />
                          <span>Delete</span>
                        </button>
                      ) : null}
                    </div>
                  }
                />
              );
            })}
          </ResultsMobileList>
          </>
        )}

        {sorted.length > 0 && !loading && (
          <TablePagination
            currentPage={currentPage}
            totalItems={sorted.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel="employees"
            rowsPerPageSelectId="portal-employees-rows-per-page"
          />
        )}
      </div>

      <PortalConfirmModal
        open={!!deactivateTarget}
        title="Deactivate Employee"
        message={
          deactivateTarget ? (
            <p>
              Deactivate <strong>{fullName(deactivateTarget)}</strong>? Their status will be set to
              Inactive.
            </p>
          ) : null
        }
        confirming={deactivatingId != null}
        confirmingLabel="Deactivating…"
        onConfirm={confirmDeactivate}
        onClose={closeDeactivateModal}
      />

        </>
      )}

      {createMode && (
        <PortalFormPage
          title="Add Employee"
          backLabel="Back to employees"
          onBack={closeAddForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Create employee"
          submittingLabel="Creating…"
        >
              <PortalAlert type="error" message={formError} onClear={() => setFormError("")} />
              <div className="cportal-form-grid">
                <div className="cportal-field">
                  <label>
                    First name <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.firstName}
                    onChange={(e) => setField("firstName", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Last name</label>
                  <input
                    value={form.lastName}
                    onChange={(e) => setField("lastName", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Company Email <span className="cportal-required">*</span>
                    <PortalFieldInfo label="About company email">
                      {portalLoginEmailHint()}
                    </PortalFieldInfo>
                  </label>
                  <input
                    type="email"
                    value={form.companyEmail}
                    onChange={(e) => setField("companyEmail", e.target.value)}
                    required
                    autoComplete="off"
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Password
                    <PortalFieldInfo label="About password">
                      Required for a new login. Leave blank if this person already signs in to
                      Cloud Office (for example at another company); they keep their current
                      password. {PASSWORD_POLICY_HINT}
                    </PortalFieldInfo>
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setField("password", e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                <div className="cportal-field">
                  <label>Personal Email</label>
                  <input
                    type="email"
                    value={form.personalEmail}
                    onChange={(e) => setField("personalEmail", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Mobile number <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.phoneNumber}
                    onChange={(e) => setField("phoneNumber", sanitizeMobileInput(e.target.value))}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit number"
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Employee ID <span className="cportal-required">*</span>
                  </label>
                  <input
                    value={form.employeeId}
                    onChange={(e) => setField("employeeId", e.target.value)}
                    required
                  />
                </div>
                <div className="cportal-field">
                  <label>Gender</label>
                  <PortalSelect
                    value={form.gender}
                    onChange={(e) => setField("gender", e.target.value)}
                  >
                    <option value="">Select gender</option>
                    {GENDER_OPTIONS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field">
                  <label>Department</label>
                  <input
                    value={form.department}
                    onChange={(e) => setField("department", e.target.value)}
                  />
                </div>
                <div className="cportal-field">
                  <label>
                    Designation <span className="cportal-required">*</span>
                  </label>
                  <PortalSelect
                    value={form.designationId}
                    onChange={(e) => setField("designationId", e.target.value)}
                    required
                  >
                    <option value="">Select designation</option>
                    {designations.map((d) => (
                      <option key={d.Designation_Id} value={d.Designation_Id}>
                        {d.Designation_Name}
                      </option>
                    ))}
                  </PortalSelect>
                </div>
                <div className="cportal-field cportal-field--full">
                  <label>
                    Roles <span className="cportal-required">*</span>
                    {domainName ? (
                      <PortalFieldInfo label="About roles">
                        Showing roles for {domainName}
                      </PortalFieldInfo>
                    ) : null}
                  </label>
                  {roles.length === 0 ? (
                    <p className="cportal-muted">No roles available.</p>
                  ) : (
                    <div className="cportal-role-checklist">
                      {roles.map((r) => {
                        const id = String(r.role_id ?? r.Role_Id);
                        return (
                          <label key={id}>
                            <input
                              type="checkbox"
                              checked={form.roleIds.includes(id)}
                              onChange={() => toggleRoleId(id)}
                            />
                            <span>{r.role_name || r.Role_Name}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="cportal-field">
                  <label>Date of joining</label>
                  <input
                    type="date"
                    value={form.doj}
                    onChange={(e) => setField("doj", e.target.value)}
                  />
                </div>
              </div>
        </PortalFormPage>
      )}
    </div>
  );
}

export default TenantEmployees;
